import { markRaw, unref } from 'vue'
import Node from '@/components/Workflows/Pages/Editor/Nodes/Node'
import {
  TreeNodeInput,
  Workflow,
  WorkflowInput,
  WorkflowRootInput,
} from '@/components/Workflows/Pages/Workflow/workflow'
import NodeConditionRule from '@/components/Workflows/Pages/Editor/Nodes/NodeConditionRule/NodeConditionRule'
import NodeApprovals from '@/components/Workflows/Pages/Editor/Nodes/NodeApprovals/NodeApprovals'
import NodeTriggerComponent from '@/components/Workflows/Pages/Editor/Nodes/NodeTrigger/NodeTriggerComponent.vue'
import { $t } from '@/composables/translation'
import { NodeTypes } from '@/components/Workflows/Pages/Editor/enums'
import { deepClone } from '@/utils/tools'

export default class NodeTrigger extends Node {
  add() {
    // This covers the edge case of having an existing approver group
    if (this.children.length === 1 && this.children[0].name === NodeTypes.NODE_APPROVALS) {
      const childNode: Node = this.children[0]
      this.children.pop() // Apparently ts is bad at understanding pop which is why we do it here
      const node = new NodeConditionRule(this, [], {
        children: [childNode],
      })

      childNode.updateParent(node)

      // We do the key flipping to avoid triggering a reanimation of the tree node which may
      // be confusing to the user. This should only trigger an animation of the child node.
      const childKey = childNode.key
      childNode.key = node.key
      node.key = childKey

      this.addChild(node)
    } else {
      const node = new NodeConditionRule(this)
      const childNode: Node = new NodeApprovals(node, [])
      node.addChild(childNode)
      this.addChild(node)
    }
  }

  compile(): WorkflowInput {
    const base = super.compile()

    const workflow: Workflow = this.vModel

    const rootInput: WorkflowRootInput = {
      children: this.children.map((c) => c.key),
      parent: undefined,
      id: this.id,
      data: {
        // I don't use the spread operator, since it adds messy keys that would need to be
        // filtered out later before sending as input
        name: workflow.name,
        description: workflow.description,
        status: workflow.status,
        trigger: workflow.trigger,
      },
    }

    base.root = [rootInput]

    return base
  }

  constructor(workflow: Workflow, options?: Partial<TreeNodeInput>) {
    super({
      name: NodeTypes.NODE_TRIGGER,
      content: $t('Custom rule'),
      key: workflow.id,
      root: true, // We are usually the root node
      canAddNode: true,
      vModel: deepClone(unref(workflow)), // workflow can be a reference else where and could be gql cache object
      component: markRaw(NodeTriggerComponent),
      children: [],
      ...options,
    })
  }
}
